import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSpreadsheet, ArrowRight, Eye } from "lucide-react";

import { getMyStandaloneAssignments } from "../../api/markschedule/standaloneMarksPortalApi";
import AssignmentStatusBadge from "../../components/markschedule/AssignmentStatusBadge";
import { isAssignmentLocked } from "../../utils/markschedule/marksConstants";
import { pickList, toId } from "../../utils/markschedule/marksHelpers";
import {
  Button,
  Card,
  CardContent,
  Alert,
  EmptyState,
  Skeleton,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const StandaloneMyAssignments = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await getMyStandaloneAssignments();
        setAssignments(pickList(res, ["assignments", "data"]));
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          My Standalone Marks Tasks
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          View and complete non-standard and standalone evaluation schemes assigned to you.
        </p>
      </div>

      {/* Error Alert */}
      

      {/* Tasks List */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton height={88} width="100%" />
          <Skeleton height={88} width="100%" />
          <Skeleton height={88} width="100%" />
        </div>
      ) : assignments.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={FileSpreadsheet}
              title="No standalone tasks found"
              description="You do not have any active standalone evaluation assignments at this time."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {assignments.map((a) => {
            const id = toId(a);
            const locked = isAssignmentLocked(a.status);
            return (
              <Card
                key={id}
                className="hover:border-neutral-300 transition-colors"
              >
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-base">
                        {a.schemeName || a.schemeId?.name || "Scheme"}
                      </h3>
                      <AssignmentStatusBadge status={a.status} />
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>
                        {a.classId?.className} · {a.sectionId?.sectionName}
                      </span>
                      <span>·</span>
                      <span>Max {a.maxMarks}</span>
                    </div>
                  </div>

                  <div className="flex items-center shrink-0">
                    <Button
                      type="button"
                      variant={locked ? "secondary" : "outline-primary"}
                      size="sm"
                      onClick={() =>
                        navigate(`/staff-panel/marks/standalone/entry/${id}`)
                      }
                      rightIcon={
                        locked ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <ArrowRight className="h-4 w-4" />
                        )
                      }
                      className="w-full sm:w-auto"
                    >
                      {locked ? "View" : "Enter Marks"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StandaloneMyAssignments;